import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CanActivateChildFn, CanActivateFn } from '@angular/router';
import { map, of, skip, switchMap, take } from 'rxjs';
import AuthDialog from './auth.dialog';
import { TrinityAuthService } from './trinity.auth.service';

export { loggedWithRoles as loggedWithRoles };

/**
 * Creates a route guard that ensures users are authenticated and have specific roles.
 *
 * This function generates a CanActivateFn that implements a sophisticated authentication
 * and authorization flow. It checks if the user is logged in and has the required roles,
 * and if not, displays an authentication dialog and waits for successful authentication.
 *
 * Authentication Flow:
 * 1. Check if user is authenticated via auth.user$ observable
 * 2. If authenticated, verify user has required roles
 * 3. If not authenticated or lacking roles, open AuthDialog
 * 4. Wait for authentication to complete
 * 5. Close dialog and allow/deny route access based on result
 *
 * The guard handles several scenarios:
 * - User is authenticated with correct roles → Allow access immediately
 * - User is authenticated but lacks roles → Show dialog for role elevation
 * - User is not authenticated → Show dialog for initial authentication
 * - Authentication fails or is cancelled → Deny access
 *
 * @param roles - Variable number of OAuth roles required for access
 * @returns A route guard function that can be used in Angular routing configuration
 *
 * @example
 * ```typescript
 * // Protect route requiring any authenticated user
 * {
 *   path: 'dashboard',
 *   component: DashboardComponent,
 *   canActivate: [loggedWithRoles()]
 * }
 *
 * // Protect route requiring domain admin role
 * {
 *   path: 'admin',
 *   component: AdminComponent,
 *   canActivate: [loggedWithRoles('google_domain_admin')]
 * }
 *
 * // Protect route requiring multiple roles (user needs ANY of these)
 * {
 *   path: 'management',
 *   component: ManagementComponent,
 *   canActivate: [loggedWithRoles('google_domain_admin', 'domain_operator')]
 * }
 *
 * // Protect child routes
 * {
 *   path: 'settings',
 *   component: SettingsComponent,
 *   canActivateChild: [loggedWithRoles('google_domain_admin')],
 *   children: [...]
 * }
 * ```
 */
function loggedWithRoles(...roles: Constants.Google.OAuthRoles[]): CanActivateFn | CanActivateChildFn {
  return () => {
    const auth = inject(TrinityAuthService);
    const dialog = inject(MatDialog);

    return auth.user$.pipe(
      switchMap((user) => {
        const hasRole = user?.roles.some((role) => roles.includes(role));

        if (user) {
          if (!roles.length) return of(true);
          if (hasRole) return of(true);
        }

        const ref = dialog.open(AuthDialog);
        auth.user$.pipe(skip(1), take(1)).subscribe(() => ref.close(true));
        return ref.afterClosed().pipe(map((v) => !!v));
      }),
    );
  };
}
